
import React, { useRef, useEffect, useState } from "react";
import Slider from "react-slick";
import {
  Card,
  CardMedia,
  Typography,
  Button,
  Box,
  Grid,
  Divider,
  Dialog,
  DialogContent,
  IconButton,
} from "@mui/material";
import ApartmentIcon from "@mui/icons-material/Apartment";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import OpenWithIcon from "@mui/icons-material/OpenWith";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import Skeleton from "@mui/material/Skeleton";
import { useNavigate } from "react-router-dom";


const ResidentialPropertiesCard = ({
  id,
  name,
  address,
  price,
  content,
  view = "list",
  thumbnail,
  images = [],
  nearby = [],
  amenities = [],
  facilities = [],
  perSqft,
  area,
  categoryname,
  description,
  verify,
  country,
  soldStatus,
  sold_status,
}) => {
  const isSold = soldStatus === 1 || sold_status === 1;
  const showArrows = images?.length > 1;
  const previewSliderRef = useRef(null);
  const navi = useNavigate();
  const textRef = useRef(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [imgOpen, setImgOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const videoRefs = useRef({});
  const [playingVideo, setPlayingVideo] = useState(null);
  const [loadedMedia, setLoadedMedia] = useState({});
  const [hoveredVideo, setHoveredVideo] = useState(null);

  const handleMediaLoad = (src) => {
    setLoadedMedia((prev) => ({ ...prev, [src]: true }));
  };

  const togglePlay = (e, src) => {
    e.stopPropagation();
    const video = videoRefs.current[src];
    if (video) {
      if (video.paused) {
        if (playingVideo && playingVideo !== src && videoRefs.current[playingVideo]) {
          videoRefs.current[playingVideo].pause();
        }
        video.play();
        setPlayingVideo(src);
      } else {
        video.pause();
        setPlayingVideo(null);
      }
    }
  };

  const isVideo = (url) => {
    if (typeof url !== 'string') return false;
    return /\.(mp4|webm|mkv|mov|avi)$/i.test(url);
  };

  // Build full image list: gallery images if available, else thumbnail
  const allImages = Array.isArray(images) && images.length > 0
    ? images
    : thumbnail ? [thumbnail] : ["/assets/images/placeholder_img.png"];

  const showNavArrows = allImages.length > 1;

  const handleOpenPreview = (index = 0) => {
    setActiveIndex(index);
    setImgOpen(true);
    setTimeout(() => {
      previewSliderRef.current?.slickGoTo(index);
    }, 100);
  };

  const visibleAmenities = amenities.slice(0, 3);
  const visibleNearby = nearby.slice(0, 3);

  useEffect(() => {
    if (textRef.current) {
      setIsOverflowing(textRef.current.scrollHeight > textRef.current.clientHeight);
    }
  }, []);

  const handleCardClick = () => {
    navi(`/flats-villas/details/${id}`);
  };

  return (
    <>
      {/* MULTI-IMAGE POPUP DIALOG */}
      <Dialog
        open={imgOpen}
        onClose={() => setImgOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            overflow: "hidden",
            bgcolor: "rgba(0,0,0,0.85)",
          },
        }}
      >
        <IconButton
          onClick={() => setImgOpen(false)}
          sx={{
            position: "absolute",
            top: { xs: 10, sm: 16 },
            right: { xs: 10, sm: 20 },
            zIndex: 10,
            color: "#fff",
            bgcolor: "rgba(0,0,0,0.4)",
            "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ position: "relative", height: { xs: 300, sm: 480, md: 600 }, overflow: "hidden" }}>
            {showNavArrows && (
              <IconButton
                onClick={() => previewSliderRef.current?.slickPrev()}
                sx={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", zIndex: 3, color: "#fff", bgcolor: "rgba(0,0,0,0.5)", "&:hover": { bgcolor: "rgba(0,0,0,0.75)" } }}
              >
                <ArrowBackIosNewIcon />
              </IconButton>
            )}
            <Box
              sx={{
                height: { xs: 300, sm: 480, md: 600 },
                "& .slick-slider, & .slick-list, & .slick-track, & .slick-slide > div": {
                  height: "100% !important",
                },
              }}
            >
              <Slider
                ref={previewSliderRef}
                initialSlide={activeIndex}
                beforeChange={(old, next) => {
                  Object.values(videoRefs.current).forEach((vid) => {
                    if (vid) vid.pause();
                    setPlayingVideo(null);
                    setActiveIndex(next);
                  });
                }}
                dots
                infinite={false}
                speed={400}
                slidesToShow={1}
                slidesToScroll={1}
                arrows={false}
              >
              {allImages.map((img, i) => (
                <Box key={i}>
                  <Box sx={{ height: { xs: 300, sm: 480, md: 600 }, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "rgba(0,0,0,0.7)", p: 1, position: "relative" }}>
                    {!loadedMedia[img] && (
                      <Skeleton
                        variant="rectangular"
                        width="100%"
                        height="100%"
                        sx={{ position: "absolute", top: 0, left: 0, bgcolor: "rgba(255,255,255,0.1)" }}
                      />
                    )}
                    {isVideo(img) ? (
                      <Box sx={{ position: "relative", width: "100%", height: "100%" }}>
                        <video
                          ref={(el) => (videoRefs.current[img] = el)}
                          src={img}
                          playsInline
                          disablePictureInPicture
                          disableRemotePlayback
                          controlsList="nodownload nopictureinpicture"
                          onLoadedData={() => handleMediaLoad(img)}
                          onClick={(e) => togglePlay(e, img)}
                          style={{ width: "100%", height: "100%", objectFit: "contain", display: loadedMedia[img] ? "block" : "none", cursor: "pointer" }}
                        />
                        {/* Standard Rectangular Play/Pause Buttons */}
                        {loadedMedia[img] && (
                          <Box
                            onClick={(e) => togglePlay(e, img)}
                            sx={{
                              position: "absolute",
                              top: "50%",
                              left: "50%",
                              transform: "translate(-50%, -50%)",
                              bgcolor: "rgba(0,0,0,0.5)",
                              color: "white",
                              width: 60,
                              height: 50,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              cursor: "pointer",
                              borderRadius: "4px",
                              zIndex: 2,
                            }}
                          >
                            {playingVideo === img ? (
                              <PauseIcon sx={{ fontSize: 40 }} />
                            ) : (
                              <PlayArrowIcon sx={{ fontSize: 40 }} />
                            )}
                          </Box>
                        )}
                      </Box>
                    ) : (
                      <img
                        src={img}
                        alt={`${name || ""} ${i + 1}`}
                        onLoad={() => handleMediaLoad(img)}
                        style={{ width: "100%", height: "100%", objectFit: "contain", display: loadedMedia[img] ? "block" : "none" }}
                      />
                    )}
                  </Box>
                </Box>
              ))}
            </Slider>
            {showNavArrows && (
              <IconButton
                onClick={() => previewSliderRef.current?.slickNext()}
                sx={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", zIndex: 3, color: "#fff", bgcolor: "rgba(0,0,0,0.5)", "&:hover": { bgcolor: "rgba(0,0,0,0.75)" } }}
              >
                <ArrowForwardIosIcon />
              </IconButton>
            )}
            {/* slide counter badge */}
            {showNavArrows && (
              <Box sx={{ position: "absolute", bottom: 12, left: "50%", transform: "translateX(-50%)", bgcolor: "rgba(0,0,0,0.5)", color: "#fff", borderRadius: "12px", px: 1.5, py: 0.3, fontSize: 12, pointerEvents: "none", zIndex: 3 }}>
                {activeIndex + 1} / {allImages.length}
              </Box>
            )}
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      <Card
        onClick={handleCardClick}
        sx={{
          borderRadius: 4,
          p: 2,
          width: "100%",
          backgroundColor: "#FBFBF8",
          boxShadow: "0 4px 12px 6px rgba(0,0,0,0.08)",
          cursor: "pointer",
          transition: "transform 0.22s ease, box-shadow 0.22s ease",
          "&:hover": {
            transform: "translateY(-4px) scale(1.01)",
            boxShadow: "0 10px 28px rgba(0,0,0,0.14)",
          },
        }}
      >
        {/* HEADER NAME + ADDRESS */}
        <Box
          sx={{
            opacity: isSold ? 0.65 : 1,
            backgroundColor: "#FFFFFFFC",
            boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            p: 2.5,
          }}
        >
          <Typography variant="h6" fontWeight={600}>
            {name || "Property Name"}
          </Typography>
          <Typography
            variant="body2"
            color="#707070"
            sx={{
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {address} {Number(country) === 3 ? ", India" : Number(country) === 2 ? ", Dubai" : ""}
          </Typography>
          <Box>
            {verify === 1 && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, mt: 1 }}>
                <img src="/assets/images/verified.png" alt="verified" width={20} height={20} />
                <Typography fontSize={{ xs: 12, sm: 13 }} fontWeight={600}>
                  Verified Plot
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        <Divider flexItem sx={{ borderStyle: "3px solid", borderColor: "#95B160", my: 2 }} />

        {/* CATEGORY + PRICE INFO ROW */}
        <Grid
          container
          sx={{ mb: 2, display: "flex", justifyContent: "space-between", alignItems: "center", opacity: isSold ? 0.65 : 1 }}
        >
          {categoryname && (
            <Box sx={{ flex: 1, display: "flex", justifyContent: "center" }}>
              <Button
                size="contain"
                variant="contained"
                onClick={(e) => e.stopPropagation()}
                sx={{ bgcolor: "#95B160", borderRadius: 2, textTransform: "none", whiteSpace: "nowrap" }}
              >
                <ApartmentIcon fontSize="small" /> {categoryname || "Villa"}
              </Button>
            </Box>
          )}
          {country && (
            <Box sx={{ flex: 1, display: "flex", justifyContent: "center" }}>
              <Button
                size="small"
                variant="outlined"
                sx={{
                  color: "#95B160",
                  borderColor: "#95B160",
                  borderRadius: 2,
                  textTransform: "none",
                  whiteSpace: "nowrap",
                  fontWeight: 600,
                  "&:hover": { borderColor: "#95B160", bgcolor: "rgba(149, 177, 96, 0.04)" }
                }}
              >
                <LocationOnIcon fontSize="small" /> {Number(country) === 3 ? "India" : Number(country) === 2 ? "Dubai" : "Other"}
              </Button>
            </Box>
          )}
          <Divider
            orientation="vertical"
            flexItem
            sx={{ borderStyle: "dashed", borderColor: "#95B160", mx: 2 }}
          />
          {/* Total Price */}
          <Box
            sx={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "flex-start" }}
          >
            <Box display="flex" alignItems="center" gap={1} sx={{ color: "#95B160" }}>
              <CurrencyRupeeIcon />
              <Typography fontWeight={600}>{price || "-"}</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" mt={1}>
              Total Price
            </Typography>
          </Box>
          <Divider
            orientation="vertical"
            flexItem
            sx={{ borderStyle: "dashed", borderColor: "#95B160", mx: 2 }}
          />
          {/* Per Sqft */}
          <Box
            sx={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "flex-start" }}
          >
            <Box display="flex" alignItems="center" gap={1} sx={{ color: "#95B160" }}>
              <CurrencyRupeeIcon />
              <Typography fontWeight={600}>{perSqft || "-"}</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" mt={1}>
              Per Sq.ft
            </Typography>
          </Box>
          <Divider
            orientation="vertical"
            flexItem
            sx={{ borderStyle: "dashed", borderColor: "#95B160", mx: 2 }}
          />
          {/* Total Sqft */}
          <Box
            sx={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "flex-start" }}
          >
            <Box display="flex" alignItems="center" gap={1} sx={{ color: "#95B160" }}>
              <OpenWithIcon />
              <Typography fontWeight={600}>{area || "-"}</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" mt={1}>
              Total Sq.ft
            </Typography>
          </Box>
        </Grid>

        <Divider flexItem sx={{ borderStyle: "3px solid", borderColor: "#95B160", my: 2 }} />

        {/* IMAGE + CONTENT ROW */}
        <Grid container spacing={0} sx={{ flexWrap: { xs: "wrap", md: "nowrap" } }}>
          {/* Image Slider */}
          <Grid
            item
            xs={12}
            md={5}
            sx={{ flexBasis: { md: "40%" }, maxWidth: { md: "40%" } }}
            onClick={(e) => e.stopPropagation()}
          >
            <Box
              sx={{
                position: "relative",
                width: "100%",
                py: 0,
              }}
            >
              {isSold && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 12,
                    left: 12,
                    bgcolor: "#d32f2f",
                    color: "#fff",
                    px: 2,
                    py: 0.6,
                    borderRadius: 1.5,
                    fontSize: 13,
                    fontWeight: 800,
                    zIndex: 3,
                    textTransform: "uppercase",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.45)",
                    letterSpacing: "0.5px",
                  }}
                >
                  Sold Out
                </Box>
              )}
              <Box
                sx={{
                  px: 1,
                  opacity: isSold ? 0.6 : 1,
                  filter: isSold ? "grayscale(20%)" : "none",
                }}
              >
                {isVideo(allImages[0]) ? (
                  <Box sx={{ position: "relative", width: "100%", height: 170 }}>
                    {!loadedMedia[allImages[0]] && (
                      <Skeleton
                        variant="rectangular"
                        width="100%"
                        height="100%"
                        sx={{ position: "absolute", top: 0, left: 0, borderRadius: 3 }}
                      />
                    )}
                    <Box
                      onMouseEnter={() => setHoveredVideo(allImages[0])}
                      onMouseLeave={() => setHoveredVideo(null)}
                      sx={{ position: "relative", width: "100%", height: "100%" }}
                    >
                      <Box
                        component="video"
                        ref={(el) => (videoRefs.current[allImages[0]] = el)}
                        src={allImages[0]}
                        playsInline
                        disablePictureInPicture
                        disableRemotePlayback
                        controlsList="nodownload nopictureinpicture"
                        preload="metadata"
                        onLoadedData={() => handleMediaLoad(allImages[0])}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (playingVideo !== allImages[0]) {
                            togglePlay(e, allImages[0]);
                          } else {
                            handleOpenPreview(0);
                          }
                        }}
                        sx={{
                          width: "100%",
                          height: 170,
                          borderRadius: 3,
                          objectFit: "cover",
                          cursor: "pointer",
                          display: loadedMedia[allImages[0]] ? "block" : "none",
                          transition: "transform 0.3s ease",
                          "&:hover": { transform: "scale(1.04)" },
                        }}
                      />
                      {loadedMedia[allImages[0]] && (
                        <Box
                          onClick={(e) => togglePlay(e, allImages[0])}
                          onMouseEnter={() => setHoveredVideo(allImages[0])}
                          onMouseLeave={() => setHoveredVideo(null)}
                          sx={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            bgcolor: "rgba(0,0,0,0.5)",
                            color: "white",
                            width: 50,
                            height: 40,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                            borderRadius: "4px",
                            opacity: playingVideo === allImages[0] ? (hoveredVideo === allImages[0] ? 1 : 0) : 1,
                            transition: "opacity 0.2s ease",
                            zIndex: 2,
                          }}
                        >
                          {playingVideo === allImages[0] ? (
                            <PauseIcon sx={{ fontSize: 30 }} />
                          ) : (
                            <PlayArrowIcon sx={{ fontSize: 30 }} />
                          )}
                        </Box>
                      )}
                    </Box>
                  </Box>
                ) : (
                  <Box sx={{ position: "relative", width: "100%", height: 170 }}>
                    {!loadedMedia[allImages[0]] && (
                      <Skeleton
                        variant="rectangular"
                        width="100%"
                        height="100%"
                        sx={{ position: "absolute", top: 0, left: 0, borderRadius: 3 }}
                      />
                    )}
                    <CardMedia
                      component="img"
                      image={allImages[0]}
                      onLoad={() => handleMediaLoad(allImages[0])}
                      onClick={(e) => { e.stopPropagation(); handleOpenPreview(0); }}
                      sx={{
                        width: "100%",
                        height: 170,
                        borderRadius: 3,
                        objectFit: "cover",
                        cursor: "zoom-in",
                        transition: "transform 0.3s ease",
                        "&:hover": { transform: "scale(1.04)" },
                        display: loadedMedia[allImages[0]] ? "block" : "none",
                      }}
                    />
                  </Box>
                )}
              </Box>
            </Box>
          </Grid>

          {/* Right-side Content */}
          <Grid
            item
            xs={12}
            md={7}
            sx={{
              flexBasis: { md: "60%" },
              maxWidth: { md: "60%" },
              pl: { md: 2 },
              display: "flex",
              flexDirection: "column",
              opacity: isSold ? 0.65 : 1,
            }}
          >
            {description?.trim() && (
              <Box sx={{ mt: 1 }}>
                <Typography sx={{ fontWeight: 500, fontSize: "16px", color: "text.primary" }}>
                  Description:&nbsp;
                </Typography>
                <Typography
                  ref={textRef}
                  sx={{
                    display: "-webkit-box",
                    WebkitLineClamp: expanded ? "none" : 3,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    fontSize: "14px",
                  }}
                >
                  {"\n"}
                  {description || ""}
                </Typography>
              </Box>
            )}

            {/* AMENITIES */}
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, mt: 1 }}>
              <Typography sx={{ minWidth: 90, fontWeight: 500, fontSize: 16, lineHeight: "22px", whiteSpace: "nowrap" }}>
                Amenities:
              </Typography>
              <Typography sx={{ fontSize: 14, lineHeight: "22px", color: "text.secondary", flex: 1, wordBreak: "break-word" }}>
                {visibleAmenities.length > 0
                  ? visibleAmenities.map((item, index) => (
                    <span key={item.id || index}>
                      {item.title}
                      {index !== visibleAmenities.length - 1 && " • "}
                    </span>
                  ))
                  : "No nearby amenities"}
              </Typography>
            </Box>

            {/* NEARBY */}
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, mt: 1 }}>
              <Typography sx={{ minWidth: 90, fontWeight: 500, fontSize: 16, lineHeight: "22px", whiteSpace: "nowrap" }}>
                Nearby:
              </Typography>
              <Typography sx={{ fontSize: 14, lineHeight: "22px", color: "text.secondary", flex: 1, wordBreak: "break-word" }}>
                {visibleNearby.length > 0
                  ? visibleNearby.map((item, index) => (
                    <span key={item.id || index}>
                      {item.title}
                      {index !== visibleNearby.length - 1 && " • "}
                    </span>
                  ))
                  : "No nearby locations"}
              </Typography>
            </Box>

            {/* View Details Button */}
            <Box display="flex" justifyContent="center" mt="auto" pt={2}>
              <Button
                variant="contained"
                sx={{
                  bgcolor: "#7EAE23",
                  color: "#fff",
                  borderRadius: 16,
                  px: { xs: 6, sm: 10, md: 10 },
                  py: 1,
                  textTransform: "capitalize",
                  fontSize: { xs: 13, sm: 14 },
                }}
                onClick={(e) => { e.stopPropagation(); navi(`/flats-villas/details/${id}`); }}
              >
                View Details
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Card>
    </>
  );
};

export default ResidentialPropertiesCard;
